import ActionButton from "@/components/buttons/ActionButton";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { resolveFormValidationError } from "@/utils/formUtils";
import { tInterval } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { validationFunctions, validations } from "@/utils/validationUtils";
import { Card, Col, Divider, Form, Input, Row } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../../../common/api/queries";
import { rowGutter } from "../../../../../../common/constants";
import { Permission } from "../../../../../../common/security/enums";
import { FieldConstraintViolation } from "../../../../../../common/types";
import type { UUID } from "../../../../../../typings/global";
import { useHasPermissions } from "../../../../../auth/hooks";
import ClientDrawerForm from "../../../../../client/components/drawers/ClientDrawerForm";
import ClientSearchInput from "../../../../../client/components/search/ClientSearchInput";
import { ClientFormStage, ClientSearchActionType } from "../../../../../client/enums";
import { useClientSearch } from "../../../../../client/hooks";
import { Client } from "../../../../../client/types";
import InstitutionSelect from "../../../../../enumerations/components/form/InstitutionSelect";
import ProductGroupSelect from "../../../../../enumerations/components/form/ProductGroupSelect";
import ProductSelect from "../../../../../enumerations/components/form/ProductSelect";
import { EnumerationType } from "../../../../../enumerations/enums";
import { InstitutionType } from "../../../../../institution/enums";
import { ProductFinancialSector } from "../../../../../product/enums";
import { ProductBase } from "../../../../../product/types";
import { InsuranceType } from "../../../../enums";
import {
  CreateUpdateInsurance,
  CreateUpdateInsuranceContract,
  CreateUpdateLifeInsurance,
  InsuranceContract,
} from "../../../../types";
import { isContractVerified, VEHICLE_INSURANCE_TYPES } from "../../../../utils";

const colSpan = 4;

interface Props {
  initialContract?: InsuranceContract;
  clients: (Partial<Client> | undefined)[];
  clientsCount: number;
  setClientsCount: (newClientsCount: number) => void;
  clientsViolationErrors: Map<number, FieldConstraintViolation[]>;
  insurancesCount: number;
  onClientChange: (index: number, client?: Partial<Client>) => void;
  onClientDelete: (index: number) => void;
  onClientViolationErrorsChange: (errorsMap: Map<number, FieldConstraintViolation[]>) => void;
}

export const InsuranceContractFormHeaderSection = ({
  initialContract,
  clients,
  clientsCount,
  setClientsCount,
  ...props
}: Props) => {
  const { t } = useTranslation();

  const clientSearch = useClientSearch();

  const productsEnums = useEnumeration(EnumerationType.PRODUCTS);
  const hasPrivilegedChangesPermission = useHasPermissions(Permission.PRIVILEGED_CHANGES_ON_VERIFIED_INSURANCE);

  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [processedClientIndex, setProcessedClientIndex] = useState<number>();
  const [clientStages, setClientStages] = useState<(ClientFormStage | undefined)[]>(
    initialContract?.clients.map(() => ClientFormStage.SELECTED) || [],
  );

  const form = Form.useFormInstance<CreateUpdateInsuranceContract>();
  const institutionId = Form.useWatch("institutionId", form);
  const productGroupId = Form.useWatch("productGroupId", form);

  const disablePrivilegedInputs = isContractVerified(initialContract) && !hasPrivilegedChangesPermission;

  const initialProductGroupId = productsEnums.find(
    (product) => product.id === initialContract?.product.id,
  )?.productGroupId;

  const setClientStage = (index: number, stage?: ClientFormStage) => {
    setClientStages((prevState) => {
      const updatedClientStages = [...prevState];
      updatedClientStages[index] = stage;

      return updatedClientStages;
    });
  };

  useEffect(() => {
    if (
      isDefined(processedClientIndex) &&
      clientSearch.result.keyword === form.getFieldValue(["clientIdentifiers", processedClientIndex])
    ) {
      if (clientSearch.result.data) {
        setClientStage(processedClientIndex, ClientFormStage.EXISTING);
        props.onClientChange(processedClientIndex, clientSearch.result.data);
      } else {
        setClientStage(processedClientIndex, ClientFormStage.NEW);
      }
    }
  }, [clientSearch.result]);

  const handleInstitutionIdChange = (institutionId: UUID) => {
    const productsByInstitution = productsEnums.filter((product) => product.institutionIds.includes(institutionId));

    const contract = form.getFieldsValue([
      ["productGroupId"],
      ["productId"],
      ["insurances"],
    ]) as CreateUpdateInsuranceContract;

    const productGroupId = productsByInstitution
      .map((product) => product.productGroupId)
      .find((id) => id === contract.productGroupId);

    form.setFieldsValue({
      productGroupId: productGroupId,
      productId: productsByInstitution.find((product) => product.id === contract.productId)?.id,
      insurances: (contract.insurances || []).map((insurance) => {
        if (insurance.type && VEHICLE_INSURANCE_TYPES.includes(insurance.type)) {
          const updatedInsurance = { ...insurance } as CreateUpdateInsurance & {
            coverageLimitId?: UUID;
            complicityId?: UUID;
          };
          delete updatedInsurance.coverageLimitId;
          delete updatedInsurance.complicityId;
          return updatedInsurance;
        }
        return insurance;
      }),
    });
  };

  const handleProductGroupChange = () => {
    form.setFieldsValue({ productId: undefined });
  };

  const handleProductChange = (product: ProductBase) => {
    const insurances = [...(form.getFieldValue(["insurances"]) || [])];

    [...Array(props.insurancesCount)].forEach((_, index) => {
      let updatedInsurance = { ...insurances[index] };

      if (!updatedInsurance.id) {
        updatedInsurance = { ...updatedInsurance, type: product.insuranceType };
      }

      if (updatedInsurance.type === InsuranceType.LIFE) {
        updatedInsurance = { ...updatedInsurance, tariffId: undefined } as CreateUpdateLifeInsurance;
      }

      insurances[index] = updatedInsurance;
    });

    form.setFieldsValue({ insurances: insurances as CreateUpdateInsurance[] });
  };

  const handleClientSearchActionClick = (index: number, actionType: ClientSearchActionType) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setProcessedClientIndex(index);
        setClientFormOpen(true);
        break;
      case ClientSearchActionType.DELETE:
        if (index === 0) {
          const updatedIdentifiers = [...(form.getFieldValue("clientIdentifiers") || [])];
          deselectClientOnInsurances(updatedIdentifiers[index]);
          updatedIdentifiers[index] = undefined;
          form.setFieldsValue({ clientIdentifiers: updatedIdentifiers });

          const updatedClientStages = [...clientStages];

          updatedClientStages[index] = undefined;
          setClientStages(updatedClientStages);

          deleteClientViolationErrors(index);
          setProcessedClientIndex(undefined);
          props.onClientChange(index);
        } else {
          const updatedIdentifiers = [...(form.getFieldValue("clientIdentifiers") || [])];
          const deletedIdentifiers = updatedIdentifiers.splice(index, 1);
          deselectClientOnInsurances(deletedIdentifiers[0]);
          form.setFieldsValue({ clientIdentifiers: updatedIdentifiers });

          const updatedClientStages = [...clientStages];
          updatedClientStages.splice(index, 1);
          setClientStages(updatedClientStages);

          const updatedViolationsErrors = new Map<number, FieldConstraintViolation[]>();
          props.clientsViolationErrors.forEach((value, key) => {
            if (key < index) {
              updatedViolationsErrors.set(key, value);
            } else if (key > index) {
              updatedViolationsErrors.set(key - 1, value);
            }
          });
          props.onClientViolationErrorsChange(updatedViolationsErrors);

          setProcessedClientIndex(undefined);
          setClientsCount(clientsCount - 1);
          props.onClientDelete(index);
        }
        break;
      case ClientSearchActionType.CLEAR:
        const updatedClientStages = [...clientStages];
        updatedClientStages[index] = undefined;
        setClientStages(updatedClientStages);

        deleteClientViolationErrors(index);
        props.onClientChange(index);
        break;
    }
  };

  const handleClientSearchSubmit = (value: string, index: number) => {
    form
      .validateFields([["clientIdentifiers", index]])
      .then(() => clientSearch.onSearch({ keyword: value }))
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string, index: number) => {
    if (clientStages[index]) {
      setClientStage(index, index === 0 ? undefined : ClientFormStage.EMPTY);
    }

    const identifier = clients[index]?.identifier;
    if (clients[index] && identifier) {
      deselectClientOnInsurances(identifier);
      props.onClientChange(index);
    }

    if (props.clientsViolationErrors.has(index)) {
      deleteClientViolationErrors(index);
    }

    handleClientSearchSubmit(value, index);
  };

  const handleClientAdd = () => {
    setClientStage(clientsCount, ClientFormStage.EMPTY);
    setClientsCount(clientsCount + 1);
  };

  const handleClientFormSubmit = (client: Client, index: number) => {
    setClientFormOpen(false);
    setProcessedClientIndex(undefined);
    setClientStage(index, ClientFormStage.SELECTED);
    deleteClientViolationErrors(index);
    props.onClientChange(index, client);
  };

  const getClientFormIdentifier = (index: number): string | undefined => {
    return (form.getFieldValue("clientIdentifiers") || [])[index];
  };

  const deselectClientOnInsurances = (identifier: string) => {
    if (identifier && validationFunctions.validatePinOrCrn(identifier)) {
      const contract = form.getFieldsValue(["insurances"]) as CreateUpdateInsuranceContract;

      form.setFieldsValue({
        insurances: (contract.insurances || []).map((formInsurance) => {
          const insurance = { ...formInsurance } as CreateUpdateInsurance;

          if ("insuredClientIdentifier" in insurance) {
            insurance["insuredClientIdentifier"] =
              insurance["insuredClientIdentifier"] === identifier ? undefined : insurance["insuredClientIdentifier"];
          }

          if ("vehicleHolderIdentifier" in insurance) {
            insurance["vehicleHolderIdentifier"] =
              insurance["vehicleHolderIdentifier"] === identifier ? undefined : insurance["vehicleHolderIdentifier"];
          }

          if ("vehicleOwnerIdentifier" in insurance) {
            insurance["vehicleOwnerIdentifier"] =
              insurance["vehicleOwnerIdentifier"] === identifier ? undefined : insurance["vehicleOwnerIdentifier"];
          }

          if ("vinculationClientIdentifier" in insurance) {
            insurance["vinculationClientIdentifier"] =
              insurance["vinculationClientIdentifier"] === identifier
                ? undefined
                : insurance["vinculationClientIdentifier"];
          }

          return insurance;
        }),
      });
    }
  };

  const deleteClientViolationErrors = (index: number) => {
    const updatedViolationsErrors = new Map<number, FieldConstraintViolation[]>([...props.clientsViolationErrors]);
    updatedViolationsErrors.delete(index);
    props.onClientViolationErrorsChange(updatedViolationsErrors);
  };

  return (
    <>
      <Card type="inner" className="card-box margin-top-medium" title={t("contract.sections.contractIdentification")}>
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="contractNumber"
              label={t("contract.attrs.contractNumber")}
              dependencies={["secondaryContractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull("secondaryContractNumber", t("contract.attrs.proposalNumber")),
                validations.notEqualTo("secondaryContractNumber", t("contract.attrs.proposalNumber")),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={5}>
            <Form.Item
              name="secondaryContractNumber"
              label={t("contract.attrs.proposalNumber")}
              dependencies={["contractNumber"]}
              rules={[
                validations.size(1, 64),
                validations.notNullIfOtherNull("contractNumber", t("contract.attrs.contractNumber")),
                validations.notEqualTo("contractNumber", t("contract.attrs.contractNumber")),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={5}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("common.insuranceInstitution"),
                rules: [validations.notNull],
              }}
              selectProps={{ disabled: disablePrivilegedInputs, onChange: handleInstitutionIdChange }}
              optionsProps={{
                selected: initialContract ? [initialContract.institution] : undefined,
                filterType: InstitutionType.INSURANCE_COMPANY,
              }}
            />
          </Col>

          <Col span={5}>
            <ProductGroupSelect
              formItemProps={{
                name: "productGroupId",
                label: t("contract.attrs.productGroupId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                selectedProductId: initialContract?.product.id,
                institutionId,
                hideAll: !institutionId,
                initialValuesSelected: initialContract?.institution.id === institutionId,
                filterSectors: [ProductFinancialSector.LIFE_INSURANCES, ProductFinancialSector.NON_LIFE_INSURANCES],
              }}
              selectProps={{
                placeholder: !institutionId ? t("contract.helpers.productGroupPlaceholder") : undefined,
                disabled: disablePrivilegedInputs,
                onChange: handleProductGroupChange,
              }}
            />
          </Col>

          <Col span={5}>
            <ProductSelect
              formItemProps={{
                name: "productId",
                label: t("contract.attrs.productId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                selectedInTree: {
                  selectedProductId: initialContract?.product.id,
                  condition:
                    initialContract?.institution.id === institutionId && initialProductGroupId === productGroupId,
                },
                institutionId,
                productGroupId: productGroupId,
                hideAll: !productGroupId,
                filterSectors: [ProductFinancialSector.LIFE_INSURANCES, ProductFinancialSector.NON_LIFE_INSURANCES],
                onChange: handleProductChange,
              }}
              selectProps={{
                disabled: disablePrivilegedInputs,
                placeholder: !productGroupId ? t("contract.helpers.productPlaceholder") : undefined,
              }}
            />
          </Col>
        </Row>

        <Divider titlePlacement="start">{t("contract.sections.clients")}</Divider>

        <Row gutter={rowGutter}>
          {Array.from({ length: clientsCount }, (_, clientIndex) => clientIndex).map((clientIndex) => (
            <React.Fragment key={clientIndex}>
              <ClientSearchInput
                processedType={processedClientIndex}
                violationErrors={props.clientsViolationErrors}
                inProgress={clientSearch.inProgress}
                inputColSpan={5}
                clientNameColSpan={6}
                onActionClick={handleClientSearchActionClick}
                onFocus={setProcessedClientIndex}
                onSearch={handleClientSearchSubmit}
                onChange={handleClientSearchChange}
                formItemProps={{
                  name: ["clientIdentifiers", clientIndex],
                  label: (
                    <LabelWithTooltip
                      tooltip={t("helpers.pinFormat")}
                      label={tInterval("contract.attrs.insuranceClientIdentifiers_interval", clientIndex)}
                    />
                  ),
                  rules: [
                    validations.notBlank,
                    validations.pinOrCrn,
                    validations.noRepeatedClient("clientIdentifiers"),
                  ],
                }}
                formStage={clientStages[clientIndex]}
                formType={clientIndex}
                client={clients[clientIndex]}
              />

              <Col span={clientIndex === 0 ? 13 : 1} />
            </React.Fragment>
          ))}
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <ActionButton icon="plus" onClick={handleClientAdd}>
              {t("contract.actions.addClient")}
            </ActionButton>
          </Col>
        </Row>
      </Card>

      <ClientDrawerForm<number>
        open={clientFormOpen}
        client={isDefined(processedClientIndex) ? clients[processedClientIndex] : undefined}
        initialIdentifier={isDefined(processedClientIndex) ? getClientFormIdentifier(processedClientIndex) : undefined}
        formType={processedClientIndex}
        violationErrors={props.clientsViolationErrors}
        onFormSubmit={handleClientFormSubmit}
      />
    </>
  );
};
